import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { generateAIReport } from '../sever/Api';
import { Button } from './button';

const DiSCResults = ({ results, onRetakeTest }) => {
  const [showDetailedReport, setShowDetailedReport] = useState(false);
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [aiReportError, setAiReportError] = useState(null);
  const [aiReportText, setAiReportText] = useState('');

  if (!results || !results.score) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No results available</p>
      </div>
    );
  }

  const { score } = results;
  
  // Handle both old and new data structures
  let d, i, s, c, dominantType;
  
  if (score.discScores) {
    // New structure with discScores
    d = { percentage: score.discScores.dPercentage };
    i = { percentage: score.discScores.iPercentage };
    s = { percentage: score.discScores.sPercentage };
    c = { percentage: score.discScores.cPercentage };
    dominantType = score.personalityType?.charAt(0) || 'D';
  } else {
    // Old structure
    d = score.d;
    i = score.s;
    s = score.s;
    c = score.c;
    dominantType = score.dominantType || 'D';
  }

  const getTypeDescription = (type) => {
    const descriptions = {
      'D': {
        name: 'Dominance',
        fullName: 'Dominant - The Driver',
        description: 'Direct, decisive, results-oriented, and forceful',
        characteristics: ['Confident', 'Results-driven', 'Direct', 'Decisive', 'Ambitious', 'Competitive'],
        strengths: [
          'Natural leadership abilities',
          'Quick decision making',
          'Goal-oriented approach',
          'High energy and drive',
          'Direct communication style',
          'Problem-solving skills'
        ],
        weaknesses: [
          'Can be impatient',
          'May seem aggressive',
          'Might overlook details',
          'Can be too direct'
        ],
        workStyle: 'You prefer to take charge and get results quickly. You work best with clear goals, direct communication, and when given autonomy to make decisions.',
        communicationStyle: 'Direct, concise, and results-focused. You prefer to get straight to the point.',
        motivation: 'Achievement, winning, and tangible results motivate you most.',
        color: 'bg-red-500',
        bgColor: 'bg-red-50',
        textColor: 'text-red-800'
      },
      'I': {
        name: 'Influence',
        fullName: 'Influential - The Inspirer',
        description: 'Outgoing, optimistic, people-oriented, and enthusiastic',
        characteristics: ['Optimistic', 'People-oriented', 'Enthusiastic', 'Outgoing', 'Persuasive', 'Creative'],
        strengths: [
          'Excellent communication skills',
          'Natural motivator',
          'Builds relationships easily',
          'Creative problem solving',
          'Positive attitude',
          'Team collaboration'
        ],
        weaknesses: [
          'May be too talkative',
          'Can be disorganized',
          'Might overlook details',
          'Can be overly optimistic'
        ],
        workStyle: 'You thrive in collaborative environments and enjoy building relationships. You bring energy and enthusiasm to your work and excel at motivating others.',
        communicationStyle: 'Warm, enthusiastic, and engaging. You enjoy storytelling and building rapport.',
        motivation: 'Recognition, social interaction, and positive feedback motivate you most.',
        color: 'bg-yellow-500',
        bgColor: 'bg-yellow-50',
        textColor: 'text-yellow-800'
      },
      'S': {
        name: 'Steadiness',
        fullName: 'Steady - The Supporter',
        description: 'Patient, loyal, team-oriented, and supportive',
        characteristics: ['Patient', 'Loyal', 'Team-oriented', 'Supportive', 'Reliable', 'Harmonious'],
        strengths: [
          'Excellent team player',
          'Reliable and consistent',
          'Good listener',
          'Patient and understanding',
          'Conflict resolution skills',
          'Loyal and trustworthy'
        ],
        weaknesses: [
          'May resist change',
          'Can be too accommodating',
          'Might avoid conflict',
          'Can be slow to decide'
        ],
        workStyle: 'You value stability and teamwork. You are reliable and supportive, preferring consistent routines, clear expectations, and collaborative environments.',
        communicationStyle: 'Patient, supportive, and diplomatic. You listen well and seek harmony.',
        motivation: 'Stability, security, and helping others motivate you most.',
        color: 'bg-green-500',
        bgColor: 'bg-green-50',
        textColor: 'text-green-800'
      },
      'C': {
        name: 'Conscientiousness',
        fullName: 'Conscientious - The Analyzer',
        description: 'Analytical, precise, quality-oriented, and systematic',
        characteristics: ['Analytical', 'Precise', 'Quality-oriented', 'Systematic', 'Thorough', 'Logical'],
        strengths: [
          'Attention to detail',
          'Analytical thinking',
          'Quality focus',
          'Systematic approach',
          'Research skills',
          'Accuracy and precision'
        ],
        weaknesses: [
          'Can be perfectionist',
          'May over-analyze',
          'Might be too critical',
          'Can be slow to decide'
        ],
        workStyle: 'You excel at detailed work and quality control. You prefer systematic approaches, working with accurate information, and having time to analyze thoroughly.',
        communicationStyle: 'Precise, logical, and fact-based. You prefer detailed explanations.',
        motivation: 'Accuracy, quality, and logical consistency motivate you most.',
        color: 'bg-blue-500',
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-800'
      }
    };
    return descriptions[type] || descriptions['D'];
  };

  const dominantInfo = getTypeDescription(dominantType);

  const fetchAIReport = async () => {
    try {
      setAiReportLoading(true);
      setAiReportError(null);
      const payload = {
        testType: 'disc',
        scores: {
          overallScore: score.overallScore,
          personalityType: score.personalityType || dominantInfo.fullName,
          discScores: {
            d: d.percentage,
            i: i.percentage,
            s: s.percentage,
            c: c.percentage,
          },
        },
        overallScore: score.overallScore,
        overallPercentage: score.percentage || Math.round((d.percentage + i.percentage + s.percentage + c.percentage) / 4),
      };
      const { report } = await generateAIReport(payload);
      setAiReportText(report);
    } catch (err) {
      setAiReportError(err?.response?.data?.message || 'Failed to generate AI report');
    } finally {
      setAiReportLoading(false);
    }
  };

  // Sort dimensions by percentage for ranking
  const dimensions = [
    { type: 'D', info: getTypeDescription('D'), percentage: d.percentage, color: 'red' },
    { type: 'I', info: getTypeDescription('I'), percentage: i.percentage, color: 'yellow' },
    { type: 'S', info: getTypeDescription('S'), percentage: s.percentage, color: 'green' },
    { type: 'C', info: getTypeDescription('C'), percentage: c.percentage, color: 'blue' }
  ].sort((a, b) => b.percentage - a.percentage);

  const getWorkplaceRecommendations = () => {
    const recommendations = {
      'D': [
        'Seek leadership roles where you can drive results',
        'Work in fast-paced, goal-oriented environments',
        'Look for opportunities to make strategic decisions',
        'Consider project management or executive positions',
        'Find mentors who can help you develop patience'
      ],
      'I': [
        'Pursue roles in sales, marketing, or public relations',
        'Look for team-based, collaborative environments',
        'Seek positions where you can inspire and motivate others',
        'Consider training, coaching, or consulting roles',
        'Find ways to channel your creativity productively'
      ],
      'S': [
        'Excel in customer service or support roles',
        'Look for stable, team-oriented environments',
        'Consider HR, counseling, or healthcare positions',
        'Seek roles where you can help others succeed',
        'Find ways to embrace change gradually'
      ],
      'C': [
        'Pursue roles in research, analysis, or quality control',
        'Look for detail-oriented, systematic environments',
        'Consider positions in IT, finance, or engineering',
        'Seek roles where accuracy is highly valued',
        'Find ways to balance perfectionism with efficiency'
      ]
    };
    return recommendations[dominantType] || [];
  };

  const getCommunicationTips = () => {
    const tips = {
      'D': [
        'Be direct and concise in your communication',
        'Focus on results and outcomes',
        'Use bullet points and action items',
        'Avoid unnecessary small talk',
        'Be prepared for quick decision-making'
      ],
      'I': [
        'Show enthusiasm and energy in your communication',
        'Use stories and examples to illustrate points',
        'Build rapport before getting to business',
        'Be open to brainstorming and collaboration',
        'Show appreciation for others\' contributions'
      ],
      'S': [
        'Be patient and listen actively',
        'Show support and understanding',
        'Avoid rushing or pressuring',
        'Provide stability and consistency',
        'Seek consensus and harmony'
      ],
      'C': [
        'Provide detailed, accurate information',
        'Use data and facts to support your points',
        'Be thorough and systematic in explanations',
        'Allow time for analysis and consideration',
        'Focus on quality and accuracy'
      ]
    };
    return tips[dominantType] || [];
  };

  if (showDetailedReport) {
    return (
      <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg p-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Detailed DiSC Assessment Report</h2>
          <p className="text-gray-600">Comprehensive behavioral profile analysis and recommendations</p>
        </div>

        {/* Executive Summary */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-8">
          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-24 h-24 ${dominantInfo.color} text-white rounded-full text-3xl font-bold mb-4`}>
              {dominantType}
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">{dominantInfo.fullName}</h3>
            <p className="text-gray-700 mb-4 text-lg">{dominantInfo.description}</p>
            <div className="flex flex-wrap justify-center gap-2">
              {dominantInfo.characteristics.map((char, index) => (
                <span key={index} className="bg-white px-4 py-2 rounded-full text-sm font-medium text-gray-700 shadow-sm">
                  {char}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 mb-8 border border-gray-200">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">We are collecting your results</h3>
          <p className="text-gray-600 mb-4">Get a personalized, actionable writeup of your DiSC results.</p>
          {aiReportLoading && (
            <div className="flex items-center text-gray-600">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
              Generating your report...
            </div>
          )}
          {aiReportError && (
            <div className="text-red-600 text-sm mb-2">{aiReportError}</div>
          )}
          {!aiReportLoading && aiReportText && (
            <div className="prose max-w-none whitespace-pre-wrap text-gray-800 text-sm">{aiReportText}</div>
          )}
          {!aiReportLoading && !aiReportText && (
            <Button onClick={fetchAIReport} className="bg-blue-600 hover:bg-blue-700 text-white">
              Generate AI Report
            </Button>
          )}
        </div>

        {/* DiSC Profile Ranking */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Your DiSC Profile Ranking</h3>
          <div className="space-y-4">
            {dimensions.map((dim, index) => (
              <div key={dim.type} className={`${dim.info.bgColor} rounded-lg p-4 border-l-4 ${dim.info.color === 'red' ? 'border-red-500' : dim.info.color === 'yellow' ? 'border-yellow-500' : dim.info.color === 'green' ? 'border-green-500' : 'border-blue-500'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className={`text-lg font-bold ${index === 0 ? 'text-2xl' : ''}`}>#{index + 1}</span>
                    <span className="font-semibold text-gray-800">{dim.type} - {dim.info.name}</span>
                    {index === 0 && <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">Dominant</span>}
                  </div>
                  <span className="text-2xl font-bold text-gray-600">{dim.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`${dim.info.color === 'red' ? 'bg-red-500' : dim.info.color === 'yellow' ? 'bg-yellow-500' : dim.info.color === 'green' ? 'bg-green-500' : 'bg-blue-500'} h-3 rounded-full transition-all duration-500`}
                    style={{ width: `${dim.percentage}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-700 mt-2">{dim.info.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Detailed Analysis Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Strengths */}
          <div className="bg-green-50 rounded-lg p-6">
            <h4 className="font-semibold text-green-800 mb-4 text-lg">Key Strengths</h4>
            <ul className="space-y-3">
              {dominantInfo.strengths.map((strength, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-green-500 mr-3 mt-1">✓</span>
                  <span className="text-gray-700">{strength}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Areas for Development */}
          <div className="bg-orange-50 rounded-lg p-6">
            <h4 className="font-semibold text-orange-800 mb-4 text-lg">Areas for Development</h4>
            <ul className="space-y-3">
              {dominantInfo.weaknesses.map((weakness, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-orange-500 mr-3 mt-1">⚡</span>
                  <span className="text-gray-700">{weakness}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Work Style and Communication */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-blue-50 rounded-lg p-6">
            <h4 className="font-semibold text-blue-800 mb-4 text-lg">Work Style</h4>
            <p className="text-gray-700 leading-relaxed">{dominantInfo.workStyle}</p>
          </div>

          <div className="bg-purple-50 rounded-lg p-6">
            <h4 className="font-semibold text-purple-800 mb-4 text-lg">Communication Style</h4>
            <p className="text-gray-700 leading-relaxed">{dominantInfo.communicationStyle}</p>
          </div>
        </div>

        {/* Motivation */}
        <div className="bg-indigo-50 rounded-lg p-6 mb-8">
          <h4 className="font-semibold text-indigo-800 mb-4 text-lg">What Motivates You</h4>
          <p className="text-gray-700 leading-relaxed">{dominantInfo.motivation}</p>
        </div>

        {/* Workplace Recommendations */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Career & Workplace Recommendations</h3>
          <div className="bg-gray-50 rounded-lg p-6">
            <ul className="space-y-3">
              {getWorkplaceRecommendations().map((rec, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-blue-500 mr-3 mt-1">💼</span>
                  <span className="text-gray-700">{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Communication Tips */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Communication Tips for Others</h3>
          <div className="bg-gray-50 rounded-lg p-6">
            <p className="text-gray-600 mb-4">When communicating with someone with your {dominantInfo.name} style:</p>
            <ul className="space-y-3">
              {getCommunicationTips().map((tip, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-green-500 mr-3 mt-1">💡</span>
                  <span className="text-gray-700">{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <button
            onClick={() => setShowDetailedReport(false)}
            className="flex-1 bg-gray-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
          >
            Show Summary View
          </button>
          <button
            onClick={onRetakeTest}
            className="flex-1 bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
          >
            Take Test Again
          </button>
          <Link
            to="/dashboard"
            className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center"
          >
            Back to Dashboard
          </Link>
        </div>

        {/* Test Information */}
        <div className="pt-6 border-t border-gray-200">
          <div className="text-center text-sm text-gray-500">
            <p>Test completed on {new Date(results.takenAt).toLocaleDateString()}</p>
            <p>Your results are automatically saved and can be viewed anytime</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">DiSC Assessment Results</h2>
        <p className="text-gray-600">Your behavioral profile analysis</p>
      </div>

      {/* Dominant Type Display */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-8 text-center">
        <div className={`inline-flex items-center justify-center w-20 h-20 ${dominantInfo.color} text-white rounded-full text-2xl font-bold mb-4`}>
          {dominantType}
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">{dominantInfo.fullName}</h3>
        <p className="text-gray-700 mb-4">{dominantInfo.description}</p>
        <div className="flex flex-wrap justify-center gap-2">
          {dominantInfo.characteristics.map((char, index) => (
            <span key={index} className="bg-white px-3 py-1 rounded-full text-sm font-medium text-gray-700 shadow-sm">
              {char}
            </span>
          ))}
        </div>
      </div>

      {/* DiSC Dimensions Chart */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Your DiSC Profile</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* D - Dominance */}
          <div className="bg-red-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-red-800">D - Dominance</span>
              <span className="text-2xl font-bold text-red-600">{d.percentage}%</span>
            </div>
            <div className="w-full bg-red-200 rounded-full h-3">
              <div 
                className="bg-red-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${d.percentage}%` }}
              ></div>
            </div>
            <p className="text-sm text-red-700 mt-2">Direct, decisive, results-oriented</p>
          </div>

          {/* I - Influence */}
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-yellow-800">I - Influence</span>
              <span className="text-2xl font-bold text-yellow-600">{i.percentage}%</span>
            </div>
            <div className="w-full bg-yellow-200 rounded-full h-3">
              <div 
                className="bg-yellow-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${i.percentage}%` }}
              ></div>
            </div>
            <p className="text-sm text-yellow-700 mt-2">Outgoing, optimistic, people-oriented</p>
          </div>

          {/* S - Steadiness */}
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-green-800">S - Steadiness</span>
              <span className="text-2xl font-bold text-green-600">{s.percentage}%</span>
            </div>
            <div className="w-full bg-green-200 rounded-full h-3">
              <div 
                className="bg-green-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${s.percentage}%` }}
              ></div>
            </div>
            <p className="text-sm text-green-700 mt-2">Patient, loyal, team-oriented</p>
          </div>

          {/* C - Conscientiousness */}
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-blue-800">C - Conscientiousness</span>
              <span className="text-2xl font-bold text-blue-600">{c.percentage}%</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-3">
              <div 
                className="bg-blue-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${c.percentage}%` }}
              ></div>
            </div>
            <p className="text-sm text-blue-700 mt-2">Analytical, precise, quality-oriented</p>
          </div>
        </div>
      </div>

      {/* Quick Analysis */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Quick Analysis</h3>
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Strengths</h4>
              <ul className="text-gray-700 space-y-1">
                {dominantInfo.characteristics.slice(0, 3).map((char, index) => (
                  <li key={index} className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    {char}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Work Style</h4>
              <p className="text-gray-700">
                {dominantInfo.workStyle}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <button
          onClick={() => setShowDetailedReport(true)}
          className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors"
        >
          View Detailed Report
        </button>
        <button
          onClick={onRetakeTest}
          className="flex-1 bg-gray-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
        >
          Take Test Again
        </button>
        <Link
          to="/dashboard"
          className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center"
        >
          Back to Dashboard
        </Link>
      </div>

      {/* Test Information */}
      <div className="pt-6 border-t border-gray-200">
        <div className="text-center text-sm text-gray-500">
          <p>Test completed on {new Date(results.takenAt).toLocaleDateString()}</p>
          <p>Your results are automatically saved and can be viewed anytime</p>
        </div>
      </div>
    </div>
  );
};

export default DiSCResults;
